import {
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  QuestionCircleOutlined,
  UserOutlined
} from "@ant-design/icons";
import { Avatar, Button, Popconfirm, Space, Table, Tag, Tooltip, Typography, theme } from "antd";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";

import CreateEditProfileModal from "./CreateEditProfileModal";
import { PROFILE_TYPE_UI_CONFIG } from "./helper";
import { showErrorNotification, showSuccessNotification } from "../../utils/notification";

const { Text } = Typography;
const { useToken } = theme;

const WebsiteFeaturedProfilesManagementDash = ({ profileType, userConfig, ROOT_URL }) => {
  const { token } = useToken();
  const { accessKey, entityLabel, pageTitle, pageDescription, typeColumns } =
    PROFILE_TYPE_UI_CONFIG[profileType];
  const hasManagementAccess = userConfig?.[accessKey]?.hasManagementAccess || false;
  const BASE_URL = `admin/website-featured-profiles/${profileType}`;

  const [profilesList, setProfilesList] = useState([]);
  const [initialValues, setInitialValues] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchProfiles = useCallback(() => {
    setLoading(true);
    axios
      .get(`${ROOT_URL}/${BASE_URL}/list`)
      .then(({ data = [] }) => {
        setProfilesList(
          data.map(({ uniquePublicId, ...profile }, index) => ({
            ...profile,
            uniquePublicId,
            key: uniquePublicId,
            index: index + 1
          }))
        );
      })
      .catch((err) => showErrorNotification(err))
      .finally(() => setLoading(false));
  }, [ROOT_URL, BASE_URL]);

  const handleSaveProfile = (formValues) => {
    const { key: profileUniquePublicId } = initialValues || {};
    const isUpdate = Boolean(profileUniquePublicId);
    const request = isUpdate
      ? axios.patch(`${ROOT_URL}/${BASE_URL}/${profileUniquePublicId}`, formValues)
      : axios.post(`${ROOT_URL}/${BASE_URL}`, formValues);

    request
      .then(() => {
        showSuccessNotification(`${entityLabel} ${isUpdate ? "updated" : "created"} successfully!`);
        setIsModalOpen(false);
        fetchProfiles();
      })
      .catch((err) => showErrorNotification(err));
  };

  const handleDeleteProfile = (profileUniquePublicId) => {
    axios
      .delete(`${ROOT_URL}/${BASE_URL}/${profileUniquePublicId}`)
      .then(() => {
        showSuccessNotification(`${entityLabel} deleted successfully`);
        fetchProfiles();
      })
      .catch((err) => showErrorNotification(err));
  };

  const columns = [
    {
      title: "S No.",
      dataIndex: "index",
      key: "index",
      width: 60,
      align: "center",
      fixed: "left"
    },
    {
      title: entityLabel,
      key: "profile",
      width: 220,
      render: ({ displayImageUrl, displayName }) => (
        <Space>
          <Avatar src={displayImageUrl || undefined} icon={<UserOutlined />} />
          <Text strong style={{ color: token.colorText }}>
            {displayName}
          </Text>
        </Space>
      )
    },
    {
      title: "Linked Account",
      dataIndex: "userEmail",
      key: "userEmail",
      width: 200,
      render: (userEmail) =>
        userEmail ? (
          <Text style={{ color: token.colorText }}>{userEmail}</Text>
        ) : (
          <Text type="secondary">—</Text>
        )
    },
    ...typeColumns,
    {
      title: "Social Links",
      key: "socialLinks",
      width: 170,
      render: ({ socialLinks = [] }) => {
        if (socialLinks.length === 0) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {socialLinks.map(({ platform, url }) => (
              <a key={`${platform}::${url}`} href={url} target="_blank" rel="noreferrer">
                <Tag icon={<LinkOutlined />} color="blue" style={{ margin: 0 }}>
                  {platform}
                </Tag>
              </a>
            ))}
          </div>
        );
      }
    },
    {
      title: "Order",
      dataIndex: "displayOrder",
      key: "displayOrder",
      width: 80,
      align: "center",
      sorter: ({ displayOrder: firstOrder = 0 }, { displayOrder: secondOrder = 0 }) =>
        firstOrder - secondOrder
    },
    {
      title: "Actions",
      key: "action",
      fixed: "right",
      width: 100,
      align: "center",
      render: (profile) => {
        const { key: profileUniquePublicId } = profile;

        return (
          <Space size="middle">
            <Tooltip title="Edit">
              <Button
                type="text"
                shape="circle"
                icon={<EditOutlined style={{ color: token.colorPrimary }} />}
                disabled={!hasManagementAccess}
                onClick={() => {
                  setInitialValues(profile);
                  setIsModalOpen(true);
                }}
              />
            </Tooltip>

            <Popconfirm
              title={`Delete ${entityLabel}`}
              description="Are you sure you want to delete this?"
              icon={<QuestionCircleOutlined style={{ color: "red" }} />}
              onConfirm={() => handleDeleteProfile(profileUniquePublicId)}
              okText="Yes"
              cancelText="No"
              disabled={!hasManagementAccess}
            >
              <Tooltip title="Delete">
                <Button
                  type="text"
                  shape="circle"
                  danger
                  disabled={!hasManagementAccess}
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  return (
    <div style={{ padding: "24px", minHeight: "100vh" }}>
      <div
        style={{
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          background: token.colorBgContainer
        }}
      >
        <div
          style={{
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>{pageTitle}</h2>
            <Text type="secondary">{pageDescription}</Text>
          </div>
          {hasManagementAccess && (
            <Button
              type="primary"
              size="large"
              onClick={() => {
                setInitialValues(null);
                setIsModalOpen(true);
              }}
            >
              + New {entityLabel}
            </Button>
          )}
        </div>

        <Table
          columns={columns}
          dataSource={profilesList}
          bordered
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: 1200 }}
        />
      </div>

      {isModalOpen && (
        <CreateEditProfileModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          onSubmit={handleSaveProfile}
          initialValues={initialValues}
          profileType={profileType}
          ROOT_URL={ROOT_URL}
        />
      )}
    </div>
  );
};

export default WebsiteFeaturedProfilesManagementDash;
